
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, X, Edit } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MedicalInfoEditorProps {
  medicalInfo: {
    allergies: string[];
    medications: string[];
    conditions: string[];
    bloodType: string;
  };
  onUpdate: (updatedInfo: any) => void;
}

export function MedicalInfoEditor({ medicalInfo, onUpdate }: MedicalInfoEditorProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [allergies, setAllergies] = useState(medicalInfo.allergies);
  const [medications, setMedications] = useState(medicalInfo.medications);
  const [conditions, setConditions] = useState(medicalInfo.conditions);
  const [newAllergy, setNewAllergy] = useState("");
  const [newMedication, setNewMedication] = useState("");
  const [newCondition, setNewCondition] = useState("");

  const addAllergy = () => {
    if (newAllergy.trim()) {
      setAllergies([...allergies, newAllergy.trim()]);
      setNewAllergy("");
    }
  };

  const removeAllergy = (index: number) => {
    setAllergies(allergies.filter((_, i) => i !== index));
  };

  const addMedication = () => {
    if (newMedication.trim()) {
      setMedications([...medications, newMedication.trim()]);
      setNewMedication("");
    }
  };

  const removeMedication = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  const addCondition = () => {
    if (newCondition.trim()) {
      setConditions([...conditions, newCondition.trim()]);
      setNewCondition("");
    }
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const updatedInfo = {
      ...medicalInfo,
      allergies,
      medications,
      conditions,
    };
    onUpdate(updatedInfo);
    setIsOpen(false);
    toast({
      title: "Medical Information Updated",
      description: "Your medical information has been successfully updated.",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4 mr-2" />
          Edit Medical Info
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Medical Information</DialogTitle>
          <DialogDescription>
            Update your allergies, medications, and medical conditions. This information helps your healthcare providers provide better care.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Allergies */}
          <div>
            <Label className="text-base font-medium text-red-600">Allergies</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-3">
              {allergies.map((allergy, index) => (
                <Badge key={index} variant="destructive" className="flex items-center gap-1">
                  {allergy}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => removeAllergy(index)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add new allergy"
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addAllergy()}
              />
              <Button variant="outline" onClick={addAllergy}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Medications */}
          <div>
            <Label className="text-base font-medium">Current Medications</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-3">
              {medications.map((medication, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {medication}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => removeMedication(index)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add new medication"
                value={newMedication}
                onChange={(e) => setNewMedication(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addMedication()}
              />
              <Button variant="outline" onClick={addMedication}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Medical Conditions */}
          <div>
            <Label className="text-base font-medium">Medical Conditions</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-3">
              {conditions.map((condition, index) => (
                <Badge key={index} variant="outline" className="flex items-center gap-1">
                  {condition}
                  <X 
                    className="w-3 h-3 cursor-pointer" 
                    onClick={() => removeCondition(index)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add new condition"
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCondition()}
              />
              <Button variant="outline" onClick={addCondition}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={handleSave} className="flex-1">
            Save Changes
          </Button>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
